import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { CustomValidationService } from "src/app/cbta-web/services/custom-validation.service";
import { CONSTANT } from "src/app/constant";

/** Build the top-level Step 1 Training Info form container. */
export function createTrainingInfoForm(fb: UntypedFormBuilder): UntypedFormGroup {
	console.log("create training info form");
	return fb.group({
		formType: ["", [Validators.required]],
		formName: ["", [Validators.required]],
		syllabusStartDate: [null, [Validators.required]],
		syllabusEndDate: [null, [Validators.required]],
		candidates: fb.array([]),
		flightSimulation: fb.group({
			simReg: ["", []],
			acType: ["", []],
			airRoute: ["", []],
			sectorNumber: ["", []],
			accumulatedSectors: ["", []],
		}),
	});
}

/** Step 2 container. */
export function createRatingFormContainer(fb: UntypedFormBuilder): UntypedFormGroup {
	return fb.group({
		ratingForm: fb.array([]),
	});
}

/** Step 3 container. */
export function createSignOffFormContainer(fb: UntypedFormBuilder): UntypedFormGroup {
	return fb.group({
		signOffForm: fb.array([]),
	});
}

/**
 * Build the nested 'sessionDetails' group:
 * - one child group per section (e.g., 'trainingSession', 'assesmentType')
 * - inside each, one boolean control for each detail code
 */
export function createSessionGroup(
	fb: UntypedFormBuilder,
	sessionKeys: any[] = [],
	preselectedCodes: string[] = [],
): UntypedFormGroup {
	const sessionGroup = fb.group({});

	const isChecked = (code: string) => (preselectedCodes ?? []).includes(code);

	for (const section of sessionKeys ?? []) {
		const sectionCode = section?.name?.code; // keep exact code ('assesmentType' spelling included)
		const controls: Record<string, UntypedFormControl> = {};

		for (const d of section?.details ?? []) {
			const code = typeof d === "string" ? d : d?.code;
			if (!code) continue;
			controls[code] = fb.control(isChecked(code));
		}

		sessionGroup.addControl(sectionCode, fb.group(controls));
	}

	return sessionGroup;
}

function getRankCode(rank: any): string | undefined {
	if (!rank) return undefined;
	return typeof rank === "string" ? rank : rank?.code;
}

/** Whether license fields should be disabled for the given rank and form type. */
export function shouldDisableLicenseFields(rank: any, formType: string): boolean {
	const code = getRankCode(rank);
	if (!code) return false;

	if ([CONSTANT.FORM_TYPE.cabin_crew, CONSTANT.FORM_TYPE.cabin_crew_instructor].includes(formType)) {
		return code === CONSTANT.RANK.pur || code === CONSTANT.RANK.scc;
	}

	if (formType === CONSTANT.FORM_TYPE.ts_5) {
		return code === CONSTANT.RANK.na;
	}

	return false;
}

/** Apply rank-based enable/disable rules to license fields on a candidate row. */
export function applyLicenseFieldRankRules(fg: UntypedFormGroup, formType: string): void {
	const shouldDisable = shouldDisableLicenseFields(fg.get("rank")?.value, formType);

	const licenseNo = fg.get("licenseNo");
	const licenseType = fg.get("licenseType");
	const licenseExpiryDate = fg.get("licenseExpiryDate");

	if (shouldDisable) {
		licenseNo?.setValue(null, { onlySelf: true });
		licenseNo?.disable({ onlySelf: true });
		licenseType?.setValue(null, { onlySelf: true });
		licenseType?.disable({ onlySelf: true });
		licenseExpiryDate?.setValue(null, { onlySelf: true });
		licenseExpiryDate?.disable({ onlySelf: true });
	} else {
		licenseNo?.enable({ onlySelf: true });
		licenseType?.enable({ onlySelf: true });
		licenseExpiryDate?.enable({ onlySelf: true });
	}
	fg.updateValueAndValidity({ emitEvent: false });
}

/** Candidate group (Step 1). */
/** Candidate group (Step 1). */
export function createCandidateGroup(
	fb: UntypedFormBuilder,
	cvs: CustomValidationService,
	formType: string,
): UntypedFormGroup {
	const isCC = [CONSTANT.FORM_TYPE.cabin_crew, CONSTANT.FORM_TYPE.cabin_crew_instructor].includes(formType);

	const group = fb.group({
		name: ["", [Validators.required, Validators.pattern(CONSTANT.FIELD_VALIDATION.NAME)]],
		rank: ["", [Validators.required]],
		staffNumber: [
			"",
			[
				Validators.required,
				Validators.pattern(CONSTANT.FIELD_VALIDATION.NUMERIC),
				Validators.minLength(7),
				cvs.validateStaffId, // <-- keep your existing sync validator (do NOT use async slot)
			],
		],
		pilotProfile: ["", formType == CONSTANT.FORM_TYPE.ts_1 ? [Validators.required] : []],
		licenseNo: ["", !isCC ? [Validators.required, Validators.pattern(CONSTANT.FIELD_VALIDATION.LICENSE)] : []],
		licenseType: ["", !isCC ? [Validators.required] : []], // if bound to mat-autocomplete object, keep object/null
		licenseExpiryDate: !isCC ? [new Date(), [Validators.required]] : [],
		sessionDetails: fb.group({}),
		flightSimulatorDetails: fb.group({
			acType: ["", formType == CONSTANT.FORM_TYPE.ts_1 ? [Validators.required] : []],
			accumulatedSectors: ["", formType == CONSTANT.FORM_TYPE.ts_1 ? [Validators.required] : []],
			airportRoute: ["", formType == CONSTANT.FORM_TYPE.ts_1 ? [Validators.required] : []],
			aircraftSimReg: ["", formType == CONSTANT.FORM_TYPE.ts_1 ? [Validators.required] : []],
			sectorNo: ["", formType == CONSTANT.FORM_TYPE.ts_1 ? [Validators.required] : []],
		}),
	});

	// helpers
	const resetAndDisable = (cName: string) => {
		const c = group.get(cName);
		if (!c) return;
		c.setValue("", { emitEvent: true });
		c.disable({ emitEvent: true });
		c.markAsPristine();
		c.markAsUntouched();
		c.setErrors(null);
		c.updateValueAndValidity({ emitEvent: true });
	};

	const enableOnly = (cName: string) => {
		const c = group.get(cName);
		if (!c) return;
		c.enable({ emitEvent: true });
		c.updateValueAndValidity({ emitEvent: true });
	};

	// rank-based toggles
	group.get("rank")?.valueChanges.subscribe((rank: any) => {
		// Cabin Crew and ETS-5 use rank to toggle license fields.
		// ETS-3 may include SCC/CC in the rank dropdown, but should NOT be treated as cabin crew here.
		const applies =
			isCC || formType === CONSTANT.FORM_TYPE.ts_5;
		if (!applies || formType === CONSTANT.FORM_TYPE.ts_1) return;

		if (shouldDisableLicenseFields(rank, formType)) {
			resetAndDisable("licenseNo");
			resetAndDisable("licenseType");
			resetAndDisable("licenseExpiryDate");
		} else {
			enableOnly("licenseNo");
			enableOnly("licenseType");
			enableOnly("licenseExpiryDate");
		}
	});

	// ===== staffNumber: browser online/offline handling at this level =====
	// const staff = group.get("staffNumber");

	// const applyNetState = (isOnline: boolean) => {
	//   if (!staff) return;
	//   if (isOnline) {
	//     staff.enable({ emitEvent: false });
	//   } else {
	//     staff.disable({ emitEvent: false }); // if you prefer read-only, handle in template instead
	//   }
	//   staff.updateValueAndValidity({ emitEvent: false });
	// };

	// // initial state
	// applyNetState(navigator.onLine);

	// // listeners (store refs so we can remove them)
	// const onOnline = () => applyNetState(true);
	// const onOffline = () => applyNetState(false);
	// window.addEventListener("online", onOnline);
	// window.addEventListener("offline", onOffline);

	// // expose cleanup hook for component
	// (group as any).__cleanupStaffNumberNet = () => {
	//   window.removeEventListener("online", onOnline);
	//   window.removeEventListener("offline", onOffline);
	// };

	return group;
}

/** Candidate rating group (Step 2). */
export function createCandidateRatingForm(
	fb: UntypedFormBuilder,
	candidateName: string,
	traineeAssessmentId?: string,
): UntypedFormGroup {
	return fb.group({
		traineeAssessmentId: [traineeAssessmentId ?? ""],
		candidateName: [candidateName, [Validators.required]],
		observations: fb.array([]),
	});
}

export function createCandidateSignoffForm(
	formType: string,
	fb: UntypedFormBuilder,
	args: {
		candidateName: string;
		candidateId: string;
		traineeAssessmentId: string;
		trainerName: string;
		signOffDetails: Partial<{
			ratingA: number;
			ratingB: number;
			ratingC: number;
			finalRating: number;
			category: string;
			sessionCheck: string | undefined;
			objectiveCheck: string | undefined;
			clearanceCheck: string;
			instructorLicenseNumber: string;
			useInstructorLicense: boolean;
			trainerName: string;
			traineeName: string;
			trainerSignature: any; // keep type flexible to match your upload/signature component
			traineeSignature: any;
		}>;
	},
): UntypedFormGroup {
	const s = args.signOffDetails || {};

	const isCC = [CONSTANT.FORM_TYPE.cabin_crew, CONSTANT.FORM_TYPE.cabin_crew_instructor].includes(formType);

	return fb.group({
		candidateName: [s.traineeName ?? args.candidateName, [Validators.required]],
		candidateId: [args.candidateId, [Validators.required]],
		traineeAssessmentId: [args.traineeAssessmentId, [Validators.required]],

		ratingA: [s.ratingA ?? 0, !isCC ? [Validators.required] : []],
		ratingB: [s.ratingB ?? 0, !isCC ? [Validators.required] : []],
		ratingC: [s.ratingC ?? 0, [Validators.required]],
		finalRating: [s.finalRating ?? 0, [Validators.required]],
		category: [s.category ?? null, !isCC ? [Validators.required] : []],

		sessionCheck: [s.sessionCheck ?? null, [Validators.required]],
		objectiveCheck: [s.objectiveCheck ?? null, [Validators.required]],
		clearanceCheck: [s.clearanceCheck ?? null, [Validators.required]],

		trainerName: [s.trainerName ?? args.trainerName ?? "", [Validators.required]],
		trainerSignature: [s.trainerSignature ?? null, [Validators.required]],
		traineeName: [s.traineeName ?? args.candidateName, [Validators.required]],
		traineeSignature: [
			s.traineeSignature ?? null,
			formType === (CONSTANT.FORM_TYPE.ts_1 || CONSTANT.FORM_TYPE.ts_3 || isCC) ? [Validators.required] : null,
		],

		instructorLicenseNumber: [s.instructorLicenseNumber ?? "", !isCC ? [Validators.required] : []],
		useInstructorLicense: [s.useInstructorLicense ?? true],
	});
}
